/**
 * Mediabunny-powered HLS player.
 *
 * Adapted from the `examples/media-player` example. Differences from the
 * generic example:
 *   - Accepts HLS only (uses `HLS_FORMATS`).
 *   - Lazily starts a periodic poll that updates the live edge for live
 *     streams and exposes a `subscribe()` API for callers (e.g. the clip
 *     controller) to react to changes.
 *   - Hides volume controls when there is no decodable audio track.
 *   - Always assumes the user is playing the live edge by default for live
 *     sources, seeking to `endTimestamp - refreshInterval * 1.5` on load.
 */

import {
	HLS_FORMATS,
	Input,
	UrlSource,
	CanvasSink,
	AudioBufferSink,
	WrappedAudioBuffer,
	WrappedCanvas,
} from 'mediabunny';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const AudioContextCtor: typeof AudioContext =
	window.AudioContext ?? (window as any).webkitAudioContext;

export interface HlsPlayerEvents {
	timeupdate: { currentTime: number; duration: number };
	livechange: { liveEdge: number; isLive: boolean };
	ready: undefined;
	error: { message: string };
}

export type HlsPlayerListener<K extends keyof HlsPlayerEvents> = (
	payload: HlsPlayerEvents[K],
) => void;

interface InitOptions {
	url: string;
	canvas: HTMLCanvasElement;
	playButton: HTMLButtonElement;
	playIcon: HTMLElement;
	pauseIcon: HTMLElement;
	currentTimeEl: HTMLElement;
	durationEl: HTMLElement;
	progressBarContainer: HTMLElement;
	progressBar: HTMLElement;
	volumeButton: HTMLButtonElement;
	volumeIcon: HTMLElement;
	volumeBarContainer: HTMLElement;
	volumeBar: HTMLElement;
	volumeCluster: HTMLElement;
	liveDot: HTMLButtonElement;
	controlsEl: HTMLElement;
	playerContainer: HTMLElement;
}

export class HlsPlayer {
	private readonly opts: InitOptions;
	private readonly listeners = new Map<
		keyof HlsPlayerEvents,
		Set<HlsPlayerListener<keyof HlsPlayerEvents>>
	>();

	// Playback state
	private input: Input | null = null;
	private videoSink: CanvasSink | null = null;
	private audioSink: AudioBufferSink | null = null;
	private audioContext: AudioContext | null = null;
	private gainNode: GainNode | null = null;

	private videoFrameIterator: AsyncGenerator<WrappedCanvas, void, unknown> | null =
		null;
	private audioBufferIterator: AsyncGenerator<WrappedAudioBuffer, void, unknown> | null =
		null;
	private nextFrame: WrappedCanvas | null = null;
	private queuedAudioNodes = new Set<AudioBufferSourceNode>();

	// Timeline state
	private firstTimestamp = 0;
	private endTimestamp = 0;
	private isRelativeToUnixEpoch = false;
	private isLive = false;
	private liveRefreshInterval: number | null = null;
	private playing = false;
	private playbackTimeAtStart = 0;
	private audioContextStartTime: number | null = null;

	// UI state
	private volume = 0.7;
	private volumeMuted = false;
	private draggingProgressBar = false;
	private draggingVolumeBar = false;
	private hideControlsTimeout = -1;
	private showControlsTemporarily: () => void = () => {};
	private hideControls: () => void = () => {};
	private livePollId = -1;
	private renderHandle = -1;
	private renderIntervalHandle = -1;

	// Async race protection
	private asyncId = 0;

	constructor(opts: InitOptions) {
		this.opts = opts;
		this.attachUi();
	}

	// === Public API ===

	getIsReady(): boolean {
		return this.input !== null;
	}

	getIsLive(): boolean {
		return this.isLive;
	}

	getLiveEdge(): number {
		return this.endTimestamp;
	}

	getLiveRefreshInterval(): number | null {
		return this.liveRefreshInterval;
	}

	getIsRelativeToUnixEpoch(): boolean {
		return this.isRelativeToUnixEpoch;
	}

	getFirstTimestamp(): number {
		return this.firstTimestamp;
	}

	getDuration(): number {
		return Math.max(0, this.endTimestamp - this.firstTimestamp);
	}

	/** Current playback time in the media's time base. */
	getCurrentTime(): number {
		if (this.playing && this.audioContext && this.audioContextStartTime !== null) {
			return (
				this.audioContext.currentTime -
				this.audioContextStartTime +
				this.playbackTimeAtStart
			);
		}
		return this.playbackTimeAtStart;
	}

	on<K extends keyof HlsPlayerEvents>(
		event: K,
		listener: HlsPlayerListener<K>,
	): () => void {
		let set = this.listeners.get(event);
		if (!set) {
			set = new Set();
			this.listeners.set(event, set);
		}
		set.add(listener as HlsPlayerListener<keyof HlsPlayerEvents>);
		return () => {
			set?.delete(listener as HlsPlayerListener<keyof HlsPlayerEvents>);
		};
	}

	async load(url: string): Promise<void> {
		await this.dispose();
		this.startRenderLoop();

		this.opts.playerContainer.dataset.state = 'hidden';
		this.opts.controlsEl.dataset.visible = 'false';

		this.emit('error', { message: '' });

		const localAsyncId = ++this.asyncId;

		const input = new Input({
			source: new UrlSource(url),
			formats: HLS_FORMATS,
		});

		const videoTrack = await input.getPrimaryVideoTrack();
		const audioTrack = await input.getPrimaryAudioTrack();

		if (localAsyncId !== this.asyncId) {
			return;
		}

		if (!videoTrack && !audioTrack) {
			this.fail('Stream contains no video or audio tracks.');
			return;
		}

		const tracks = [videoTrack, audioTrack].filter((t) => t !== null);
		this.firstTimestamp = Math.max(await input.getFirstTimestamp(tracks), 0);
		this.endTimestamp =
			(await input.getDurationFromMetadata(tracks, { skipLiveWait: true })) ??
			(await input.computeDuration(tracks, { skipLiveWait: true }));
		this.isRelativeToUnixEpoch = (
			await Promise.all(tracks.map((t) => t.isRelativeToUnixEpoch()))
		).some(Boolean);

		this.playbackTimeAtStart = this.firstTimestamp;

		let problemMessage = '';
		let usableVideo = videoTrack;
		let usableAudio = audioTrack;

		if (videoTrack) {
			if ((await videoTrack.getCodec()) === null) {
				problemMessage += 'Unsupported video codec. ';
				usableVideo = null;
			} else if (!(await videoTrack.canDecode())) {
				problemMessage += 'Unable to decode the video track. ';
				usableVideo = null;
			}
		}

		if (audioTrack) {
			if ((await audioTrack.getCodec()) === null) {
				problemMessage += 'Unsupported audio codec. ';
				usableAudio = null;
			} else if (!(await audioTrack.canDecode())) {
				problemMessage += 'Unable to decode the audio track. ';
				usableAudio = null;
			}
		}

		if (!usableVideo && !usableAudio) {
			this.fail(problemMessage || 'No decodable tracks found.');
			return;
		}

		this.input = input;
		this.videoSink = usableVideo
			? new CanvasSink(usableVideo, {
					poolSize: 8,
					fit: 'contain',
					alpha: await usableVideo.canBeTransparent(),
				})
			: null;
		this.audioSink = usableAudio ? new AudioBufferSink(usableAudio) : null;

		// Audio context
		this.audioContext = new AudioContextCtor({
			sampleRate: await usableAudio?.getSampleRate(),
		});
		this.gainNode = this.audioContext.createGain();
		this.gainNode.connect(this.audioContext.destination);
		this.updateVolume();

		// Canvas sizing
		if (usableVideo) {
			this.opts.canvas.style.display = '';
			this.opts.canvas.width = await usableVideo.getDisplayWidth();
			this.opts.canvas.height = await usableVideo.getDisplayHeight();
		} else {
			this.opts.canvas.style.display = 'none';
		}

		if (usableAudio) {
			this.opts.volumeCluster.hidden = false;
		} else {
			this.opts.volumeCluster.hidden = true;
		}

		this.updateDurationLabel();
		this.updateProgressBar(this.getCurrentTime());

		if (problemMessage) {
			this.emitWarning(problemMessage.trim());
		}

		// Render the first frame before showing the player
		await this.startVideoIterator();
		this.opts.playerContainer.dataset.state = 'visible';

		// Live detection / polling
		const refreshIntervals = await Promise.all(
			tracks.map((t) => t.getLiveRefreshInterval()),
		);
		const nonNullIntervals = refreshIntervals.filter(
			(v): v is number => v !== null,
		);

		if (nonNullIntervals.length > 0) {
			this.isLive = true;
			this.liveRefreshInterval = Math.min(...nonNullIntervals);
			this.opts.liveDot.hidden = false;
			this.opts.liveDot.onclick = () => {
				const interval = this.liveRefreshInterval ?? 0;
				void this.seekToTime(this.endTimestamp - interval * 1.5);
			};
			this.startLivePolling();
			// Seek close to the live edge so the user starts at the current point.
			const start = this.endTimestamp - this.liveRefreshInterval * 1.5;
			await this.seekToTime(Math.max(start, this.firstTimestamp));
		} else {
			this.isLive = false;
			this.opts.liveDot.hidden = true;
		}

		this.emit('ready', undefined);
		this.emit('livechange', {
			liveEdge: this.endTimestamp,
			isLive: this.isLive,
		});
	}

	async dispose(): Promise<void> {
		this.asyncId++;
		if (this.playing) {
			this.pause();
		}
		void this.videoFrameIterator?.return();
		void this.audioBufferIterator?.return();
		clearTimeout(this.livePollId);
		if (this.renderHandle !== -1) {
			cancelAnimationFrame(this.renderHandle);
			this.renderHandle = -1;
		}
		if (this.renderIntervalHandle !== -1) {
			clearInterval(this.renderIntervalHandle);
			this.renderIntervalHandle = -1;
		}
		try {
			await this.audioContext?.close();
		} catch {
			// Ignore errors during disposal
		}
		this.audioContext = null;
		this.gainNode = null;
		this.input = null;
		this.videoSink = null;
		this.audioSink = null;
		this.videoFrameIterator = null;
		this.audioBufferIterator = null;
		this.audioBufferIterator = null;
		this.nextFrame = null;
		this.queuedAudioNodes.forEach((node) => {
			try {
				node.stop();
			} catch {
				// Node may already be stopped
			}
		});
		this.queuedAudioNodes.clear();
	}

	// === Rendering loop ===

	private startRenderLoop(): void {
		if (this.renderHandle === -1) {
			const tick = () => {
				this.render();
				this.renderHandle = requestAnimationFrame(tick);
			};
			this.renderHandle = requestAnimationFrame(tick);
		}

		// Fallback timer to keep updates flowing even if the tab is throttled.
		if (this.renderIntervalHandle === -1) {
			this.renderIntervalHandle = window.setInterval(
				() => this.render(false),
				500,
			);
		}
	}

	private render(requestFrame = true): void {
		if (!this.input) {
			if (requestFrame) this.startRenderLoop();
			return;
		}
		const playbackTime = this.getCurrentTime();
		if (playbackTime >= this.endTimestamp) {
			this.pause();
			this.playbackTimeAtStart = this.endTimestamp;
		}

		if (this.nextFrame && this.nextFrame.timestamp <= playbackTime) {
			const ctx = this.opts.canvas.getContext('2d');
			if (ctx) {
				ctx.clearRect(0, 0, this.opts.canvas.width, this.opts.canvas.height);
				ctx.drawImage(this.nextFrame.canvas, 0, 0);
			}
			this.nextFrame = null;
			void this.advanceVideoIterator();
		}

		if (!this.draggingProgressBar) {
			this.updateProgressBar(playbackTime);
		}
	}

	private async advanceVideoIterator(): Promise<void> {
		const localAsyncId = this.asyncId;
		while (true) {
			const next = (await this.videoFrameIterator?.next())?.value ?? null;
			if (!next) return;
			if (localAsyncId !== this.asyncId) return;
			const playbackTime = this.getCurrentTime();
			if (next.timestamp <= playbackTime) {
				const ctx = this.opts.canvas.getContext('2d');
				if (ctx) {
					ctx.clearRect(
						0,
						0,
						this.opts.canvas.width,
						this.opts.canvas.height,
					);
					ctx.drawImage(next.canvas, 0, 0);
				}
			} else {
				this.nextFrame = next;
				return;
			}
		}
	}

	private async startVideoIterator(): Promise<void> {
		if (!this.videoSink) return;
		const localAsyncId = ++this.asyncId;
		await this.videoFrameIterator?.return();
		if (localAsyncId !== this.asyncId) return;
		this.videoFrameIterator = this.videoSink.canvases(this.getCurrentTime());
		const first = (await this.videoFrameIterator.next())?.value ?? null;
		const second = (await this.videoFrameIterator.next())?.value ?? null;
		if (localAsyncId !== this.asyncId) return;
		this.nextFrame = second;
		if (first) {
			const ctx = this.opts.canvas.getContext('2d');
			if (ctx) {
				ctx.clearRect(0, 0, this.opts.canvas.width, this.opts.canvas.height);
				ctx.drawImage(first.canvas, 0, 0);
			}
		}
	}

	// === Audio playback ===

	private async runAudioIterator(): Promise<void> {
		if (!this.audioSink || !this.audioContext || !this.gainNode) return;
		for await (const { buffer, timestamp } of this.audioBufferIterator!) {
			if (!this.audioContext || !this.gainNode) break;
			const node = this.audioContext.createBufferSource();
			node.buffer = buffer;
			node.connect(this.gainNode);
			let startTime =
				(this.audioContextStartTime ?? 0) + timestamp - this.playbackTimeAtStart;
			startTime =
				Math.round(this.audioContext.sampleRate * startTime) /
				this.audioContext.sampleRate;
			if (startTime >= this.audioContext.currentTime) {
				node.start(startTime);
			} else {
				node.start(this.audioContext.currentTime, this.audioContext.currentTime - startTime);
			}
			this.queuedAudioNodes.add(node);
			node.onended = () => this.queuedAudioNodes.delete(node);
			if (timestamp - this.getCurrentTime() >= 1) {
				await new Promise<void>((resolve) => {
					const id = window.setInterval(() => {
						if (timestamp - this.getCurrentTime() < 1) {
							window.clearInterval(id);
							resolve();
						}
					}, 100);
				});
			}
		}
	}

	// === Playback controls ===

	private async play(): Promise<void> {
		if (!this.audioContext) return;
		if (this.audioContext.state === 'suspended') {
			await this.audioContext.resume();
		}
		if (this.getCurrentTime() >= this.endTimestamp) {
			this.playbackTimeAtStart = this.firstTimestamp;
			await this.startVideoIterator();
		}
		this.audioContextStartTime = this.audioContext.currentTime;
		this.playing = true;

		if (this.audioSink) {
			void this.audioBufferIterator?.return();
			this.audioBufferIterator = this.audioSink.buffers(this.getCurrentTime());
			void this.runAudioIterator();
		}

		this.opts.playIcon.hidden = true;
		this.opts.pauseIcon.hidden = false;
	}

	private pause(): void {
		this.playbackTimeAtStart = this.getCurrentTime();
		this.playing = false;
		void this.audioBufferIterator?.return();
		this.audioBufferIterator = null;
		for (const node of this.queuedAudioNodes) {
			try {
				node.stop();
			} catch {
				// Node may already be stopped
			}
		}
		this.queuedAudioNodes.clear();
		this.opts.playIcon.hidden = false;
		this.opts.pauseIcon.hidden = true;
	}

	private async togglePlay(): Promise<void> {
		if (this.playing) {
			this.pause();
		} else {
			await this.play();
		}
	}

	async seekToTime(seconds: number): Promise<void> {
		const target = Math.min(Math.max(seconds, this.firstTimestamp), this.endTimestamp);
		this.updateProgressBar(target);
		const wasPlaying = this.playing;
		if (wasPlaying) this.pause();
		this.playbackTimeAtStart = target;
		await this.startVideoIterator();
		if (wasPlaying && this.playbackTimeAtStart < this.endTimestamp) {
			await this.play();
		}
	}

	// === Live polling ===

	private startLivePolling(): void {
		clearTimeout(this.livePollId);
		const tick = async () => {
			if (!this.input) return;
			const tracks = await this.input.getTracks();
			const refreshIntervals = await Promise.all(
				tracks.map((t) => t.getLiveRefreshInterval()),
			);
			const nonNull = refreshIntervals.filter(
				(v): v is number => v !== null,
			);
			if (nonNull.length === 0) {
				this.isLive = false;
				this.opts.liveDot.hidden = true;
				this.liveRefreshInterval = null;
				this.emit('livechange', {
					liveEdge: this.endTimestamp,
					isLive: false,
				});
				return;
			}
			this.liveRefreshInterval = Math.min(...nonNull);
			this.endTimestamp =
				(await this.input.getDurationFromMetadata(tracks, {
					skipLiveWait: true,
				})) ??
				(await this.input.computeDuration(tracks, { skipLiveWait: true }));
			this.updateDurationLabel();
			this.emit('livechange', {
				liveEdge: this.endTimestamp,
				isLive: true,
			});
			this.livePollId = window.setTimeout(tick, this.liveRefreshInterval * 1000);
		};
		tick();
	}

	// === UI plumbing ===

	private attachUi(): void {
		const { opts } = this;

		opts.playButton.addEventListener('click', () => void this.togglePlay());

		opts.progressBarContainer.addEventListener('pointerdown', (event) => {
			this.draggingProgressBar = true;
			opts.progressBarContainer.setPointerCapture(event.pointerId);
			this.handleProgressInteraction(event.clientX);
			window.addEventListener(
				'pointerup',
				(ev) => {
					this.draggingProgressBar = false;
					opts.progressBarContainer.releasePointerCapture(ev.pointerId);
					this.handleProgressInteraction(ev.clientX);
					void this.handleProgressCommit(ev.clientX);
				},
				{ once: true },
			);
		});
		opts.progressBarContainer.addEventListener('pointermove', (event) => {
			if (this.draggingProgressBar) {
				this.handleProgressInteraction(event.clientX);
			}
		});

		opts.volumeBarContainer.addEventListener('pointerdown', (event) => {
			this.draggingVolumeBar = true;
			opts.volumeBarContainer.setPointerCapture(event.pointerId);
			this.setVolumeFromClientX(event.clientX);
			this.volumeMuted = false;
			window.addEventListener(
				'pointerup',
				(ev) => {
					this.draggingVolumeBar = false;
					opts.volumeBarContainer.releasePointerCapture(ev.pointerId);
					this.setVolumeFromClientX(ev.clientX);
				},
				{ once: true },
			);
		});
		opts.volumeBarContainer.addEventListener('pointermove', (event) => {
			if (this.draggingVolumeBar) {
				this.setVolumeFromClientX(event.clientX);
			}
		});
		opts.volumeButton.addEventListener('click', () => {
			this.volumeMuted = !this.volumeMuted;
			this.updateVolume();
		});

		// Custom controls visibility (similar to the upstream example).
		this.hideControls = () => {
			opts.controlsEl.dataset.visible = 'false';
		};
		this.showControlsTemporarily = () => {
			if (!this.videoSink) return;
			opts.controlsEl.dataset.visible = 'true';
			window.clearTimeout(this.hideControlsTimeout);
			this.hideControlsTimeout = window.setTimeout(() => {
				if (this.draggingProgressBar) return;
				this.hideControls();
			}, 2000);
		};
		this.hideControls();
		opts.playerContainer.addEventListener('pointermove', (event) => {
			if (event.pointerType !== 'touch') this.showControlsTemporarily();
		});
		opts.playerContainer.addEventListener('pointerleave', (event) => {
			if (!this.videoSink) return;
			if (
				this.draggingProgressBar ||
				this.draggingVolumeBar ||
				event.pointerType === 'touch'
			) {
				return;
			}
			this.hideControls();
		});

		opts.playerContainer.addEventListener('click', (event) => {
			event.stopPropagation();
			if (opts.controlsEl.dataset.visible === 'true') {
				this.hideControls();
			} else {
				this.showControlsTemporarily();
			}
		});

		window.addEventListener('keydown', (event) => {
			if (!this.input) return;
			if (event.code === 'Space' || event.code === 'KeyK') {
				void this.togglePlay();
			} else if (event.code === 'ArrowLeft') {
				void this.seekToTime(this.getCurrentTime() - 5);
			} else if (event.code === 'ArrowRight') {
				void this.seekToTime(this.getCurrentTime() + 5);
			} else {
				return;
			}
			this.showControlsTemporarily();
			event.preventDefault();
		});

		this.startRenderLoop();
	}

	private handleProgressInteraction(clientX: number): void {
		const rect = this.opts.progressBarContainer.getBoundingClientRect();
		const ratio = clamp((clientX - rect.left) / rect.width, 0, 1);
		this.updateProgressBar(
			this.firstTimestamp + ratio * (this.endTimestamp - this.firstTimestamp),
		);
	}

	private handleProgressCommit(clientX: number): void {
		const rect = this.opts.progressBarContainer.getBoundingClientRect();
		const ratio = clamp((clientX - rect.left) / rect.width, 0, 1);
		const target =
			this.firstTimestamp + ratio * (this.endTimestamp - this.firstTimestamp);
		void this.seekToTime(target);
	}

	private setVolumeFromClientX(clientX: number): void {
		const rect = this.opts.volumeBarContainer.getBoundingClientRect();
		this.volume = clamp((clientX - rect.left) / rect.width, 0, 1);
		this.updateVolume();
	}

	private updateVolume(): void {
		if (!this.gainNode) return;
		const actual = this.volumeMuted ? 0 : this.volume;
		this.opts.volumeBar.style.width = `${actual * 100}%`;
		this.gainNode.gain.value = actual ** 2;
		const level = this.volumeMuted ? 0 : Math.ceil(1 + 3 * this.volume);
		this.opts.volumeIcon.textContent =
			['🔇', '🔈', '🔉', '🔊'][level] ?? '🔇';
		this.opts.volumeBarContainer.setAttribute(
			'aria-valuenow',
			this.volume.toFixed(2),
		);
	}

	private updateProgressBar(seconds: number): void {
		const ratio =
			this.endTimestamp > this.firstTimestamp
				? clamp(
						(seconds - this.firstTimestamp) /
							(this.endTimestamp - this.firstTimestamp),
						0,
						1,
					)
				: 0;
		this.opts.progressBar.style.width = `${ratio * 100}%`;
		this.opts.currentTimeEl.textContent = formatTimestamp(
			seconds,
			this.isRelativeToUnixEpoch,
		);
		this.opts.progressBarContainer.setAttribute(
			'aria-valuenow',
			Math.round(ratio * 100).toString(),
		);
		this.emit('timeupdate', {
			currentTime: seconds,
			duration: this.endTimestamp - this.firstTimestamp,
		});
	}

	private updateDurationLabel(): void {
		this.opts.durationEl.textContent = formatTimestamp(
			this.endTimestamp,
			this.isRelativeToUnixEpoch,
		);
	}

	private emit<K extends keyof HlsPlayerEvents>(
		event: K,
		payload: HlsPlayerEvents[K],
	): void {
		const set = this.listeners.get(event);
		if (!set) return;
		for (const fn of set) {
			try {
				(fn as HlsPlayerListener<K>)(payload);
			} catch (err) {
				console.error('Listener error', err);
			}
		}
	}

	private emitWarning(message: string): void {
		this.emit('error', { message });
	}

	private fail(message: string): void {
		this.opts.playerContainer.dataset.state = 'hidden';
		this.opts.controlsEl.dataset.visible = 'false';
		this.emit('error', { message });
	}
}

function clamp(value: number, min: number, max: number): number {
	return Math.min(Math.max(value, min), max);
}

function formatTimestamp(seconds: number, isUnixEpoch: boolean): string {
	if (isUnixEpoch) {
		// For live streams using #EXT-X-PROGRAM-DATE-TIME we display UTC
		// wall-clock time. Mediabunny aligns everything to the same
		// Unix-epoch seconds, so this is accurate.
		const date = new Date(seconds * 1000);
		return formatUtc(date);
	}
	return formatHms(Math.max(0, seconds));
}

function formatHms(totalSeconds: number): string {
	const seconds = Math.max(0, totalSeconds);
	const h = Math.floor(seconds / 3600);
	const m = Math.floor((seconds % 3600) / 60);
	const s = Math.floor(seconds % 60);
	const pad = (n: number) => n.toString().padStart(2, '0');
	if (h > 0) return `${h}:${pad(m)}:${pad(s)}`;
	return `${pad(m)}:${pad(s)}`;
}

function formatUtc(date: Date): string {
	const pad = (n: number) => n.toString().padStart(2, '0');
	return (
		`${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())} ` +
		`${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}:${pad(date.getUTCSeconds())}`
	);
}
